/**
 * Pure tab arithmetic for the chat page.
 *
 * No React: which panes should be mounted, and where the selection lands when
 * the tab you are looking at closes. Both were inline in ChatPage and both are
 * the kind of off-by-one that only shows up as "closing a tab dumped me on the
 * start screen".
 */

import type { ChatTab } from '../../hooks/useChatTabs.js';

/**
 * The tabs to actually render.
 *
 * Includes the selected chat even on the frame BEFORE the open-tab effect has
 * added it to the persisted list — otherwise switching to a brand-new chat
 * flashes the start screen for a frame.
 */
export function computeVisibleTabs(
  tabsEnabled: boolean,
  tabs: readonly ChatTab[],
  selectedChatId: string | null,
  now: number = Date.now()
): ChatTab[] {
  if (!tabsEnabled) return [];
  if (!selectedChatId || tabs.some((tab) => tab.id === selectedChatId)) return [...tabs];
  return [...tabs, { id: selectedChatId, title: 'New chat', lastActiveAt: now }];
}

/**
 * Where the selection goes when `closingId` is closed: its right-hand
 * neighbour, else its left-hand one, else nothing (drop to the start screen).
 * Returns `null` for "no chat selected".
 */
export function pickNeighbourTabId(
  tabs: readonly ChatTab[],
  closingId: string
): string | null {
  const index = tabs.findIndex((tab) => tab.id === closingId);
  if (index === -1) return null;
  const next = tabs[index + 1] ?? tabs[index - 1] ?? null;
  return next ? next.id : null;
}
